
import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AnimatedSection } from './AnimatedSection';
import { Button } from './Button';
import { CheckCircle2, Clock, RotateCcw, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ROUTES } from '../../constants';

interface RoadmapSectionProps {
  category?: 'corporate' | 'token' | 'finance' | 'realEstate';
  showFullLink?: boolean;
}

export const RoadmapSection: React.FC<RoadmapSectionProps> = ({ category, showFullLink = false }) => {
  const { t } = useLanguage();
  const [filter, setFilter] = useState<'all' | 'completed' | 'inprogress' | 'planned'>('all');

  // Notion Links
  const notionLinks = {
    it: 'https://www.notion.so/2191f142313180d7abc8d42ac788c7f0?v=2191f1423131804fa8fc000c13c1d182',
    en: 'https://www.notion.so/23b1f1423131808f9288ff1e4f3d8e8d?v=23b1f142313181d69411000c010d68ab&source=copy_link',
    et: 'https://www.notion.so/2411f1423131807bac31e41c1d03e77e?v=2411f142313181ec932a000c015211bc'
  };

  const roadmapItems = t('corporateInfo.roadmap.items') as any[];

  // Filter by category if provided, and then by status filter
  const filteredItems = roadmapItems.filter(item => {
    const categoryMatch = category ? item.category === category : true;
    const statusMatch = filter === 'all' || item.status === filter;
    return categoryMatch && statusMatch;
  });

  return (
    <section className="mb-24" id="roadmap">
      <AnimatedSection>
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold mb-4 uppercase tracking-wide">
            {t('corporateInfo.roadmap.titlePart1')} <span className="text-orange-500">{t('corporateInfo.roadmap.titlePart2')}</span>
          </h2>
          <p className="text-gray-400 max-w-4xl mx-auto leading-relaxed mb-4">{t('corporateInfo.roadmap.subtitle')}</p>

          {category && (
            <div className="mb-6 inline-block bg-blue-900/20 border border-blue-500/30 px-4 py-2 rounded-full">
              <p className="text-blue-300 text-sm font-medium flex items-center">
                <span className="w-2 h-2 bg-blue-500 rounded-full mr-2 animate-pulse"></span>
                {t('common.partialRoadmapNote') || "Visualizzazione filtrata per questa sezione."}
              </p>
            </div>
          )}

          <div className="flex flex-col md:flex-row items-center justify-center gap-6 mb-12">
            {/* Filters */}
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                onClick={() => setFilter('all')}
                size="sm"
                className={`${filter === 'all' ? 'bg-blue-600 text-white border-blue-600' : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'} border transition-all`}
              >
                {t('corporateInfo.roadmap.filters.all')}
              </Button>
              <Button
                onClick={() => setFilter('completed')}
                size="sm"
                className={`${filter === 'completed' ? 'bg-green-600 text-white border-green-600' : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'} border transition-all`}
              >
                <CheckCircle2 size={14} className="mr-2" />
                {t('corporateInfo.roadmap.filters.completed')}
              </Button>
              <Button
                onClick={() => setFilter('inprogress')}
                size="sm"
                className={`${filter === 'inprogress' ? 'bg-orange-500 text-white border-orange-500' : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'} border transition-all`}
              >
                <Clock size={14} className="mr-2" />
                {t('corporateInfo.roadmap.filters.inprogress')}
              </Button>
              <Button
                onClick={() => setFilter('planned')}
                size="sm"
                className={`${filter === 'planned' ? 'bg-gray-600 text-white border-gray-600' : 'bg-transparent border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'} border transition-all`}
              >
                <RotateCcw size={14} className="mr-2" />
                {t('corporateInfo.roadmap.filters.planned')}
              </Button>
            </div>

            {/* Divider on desktop */}
            <div className="hidden md:block w-px h-8 bg-gray-700"></div>

            {/* Notion Buttons Group */}
            <div className="flex flex-col items-center">
              <div className="text-[10px] uppercase text-gray-500 font-bold mb-1 tracking-wider">
                {t('corporateInfo.roadmap.notionLabel') || "Roadmap Estesa (Notion)"}
              </div>
              <div className="flex gap-2">
                <a href={notionLinks.it} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 shadow-none px-3 py-1.5 text-xs font-bold">
                    🇮🇹 IT
                  </Button>
                </a>
                <a href={notionLinks.en} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 shadow-none px-3 py-1.5 text-xs font-bold">
                    🇬🇧 EN
                  </Button>
                </a>
                <a href={notionLinks.et} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-gray-800 hover:bg-gray-700 text-white border border-gray-600 shadow-none px-3 py-1.5 text-xs font-bold">
                    🇪🇪 ET
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-[#151E32] border border-gray-800 rounded-3xl p-4 md:p-8">
          <div className="space-y-4">
            {filteredItems.map((item: any, i: number) => {
              let statusColor = "bg-gray-700";
              let statusText = "text-gray-400";
              if (item.status === 'completed') {
                statusColor = "bg-green-500";
                statusText = "text-green-500";
              } else if (item.status === 'inprogress') {
                statusColor = "bg-orange-500";
                statusText = "text-orange-500";
              }

              return (
                <div key={i} className="bg-[#0f1623] border border-gray-800 rounded-xl p-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-white">{item.title}</h4>
                      <span className={`${statusColor} text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase`}>
                        {item.statusLabel}
                      </span>
                    </div>
                    <p className="text-gray-500 text-sm">{item.desc}</p>
                    <span className="text-gray-600 text-xs mt-2 block md:hidden">{item.date}</span>
                  </div>
                  <div className="hidden md:flex flex-col items-end justify-center min-w-[100px]">
                    <div className="flex items-center justify-center w-12 h-12 rounded-full border border-gray-700 bg-[#151E32] mb-2">
                      {item.status === 'completed' ? (
                        <CheckCircle2 size={24} className="text-green-500" />
                      ) : (
                        <div className={`w-4 h-4 rounded-full ${statusColor}`}></div>
                      )}
                    </div>
                    <span className="text-gray-500 text-xs font-mono">{item.date}</span>
                  </div>
                </div>
              );
            })}
            {filteredItems.length === 0 && (
              <div className="text-center py-10 text-gray-500">
                {t('common.noRoadmapItems') || "Nessun elemento trovato per questa categoria."}
              </div>
            )}
          </div>

          {showFullLink && (
            <div className="mt-8 text-center">
              <Link to={`${ROUTES.CORPORATE_INFO}#roadmap`}>
                <Button variant="outline" className="border-gray-700 text-gray-300 hover:text-white hover:border-gray-500">
                  {t('common.viewFullRoadmap') || "Visualizza Roadmap Completa"} <ArrowRight size={16} className="ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </AnimatedSection>
    </section>
  );
};
